import React, { useEffect } from "react";
import {
  taggedSwingConfigs,
  equalPanelsSwing,
  taggedPicoSwingsConfigImgs,
  taggedPicoSwingsConfigImgsEven,
} from "../../../taggedImages/taggedSwingConfigImgs";
import {
  taggedSwingConfigsAlum,
  equalPanelsSwingAlum,
} from "../../../taggedImages/aluminum/taggedSwingConfigs";
import {
  taggedPivotConfigs,
  equalPanelsPivot,
} from "../../../taggedImages/taggedPivotConfigImgs";
import { useStore } from "../../../store/store";

interface SwingImgConfigObj {
  swingDirection: string;
  SwingType: string;
  Sidelites: string;
  label: string;
  img1: string;
  img2: string;
  img3?: string;
  img4?: string;
  img1Lever?: string;
  img2Lever?: string;
  img3Lever?: string;
  img4Lever?: string;
  img1M?: string;
  img2M?: string;
  img3M?: string;
  img4M?: string;
}

function useConfigSelector(
  isPivot: boolean,
  sType: string,
  sLite: string,
  swingDir: string,
  setWidthMeasureImg: (img: string | undefined) => void,
) {
  const addToQuote = useStore((state) => state.addToQuote);
  const handleError = useStore((state) => state.handleError);
  const exactConfig = useStore((state) => state.quote.exactConfig);
  const exactConfigImg = useStore((state) => state.quote.exactConfigImg);
  const handleStyle = useStore((state) => state.quote.handleStyle);
  const material = useStore((state) => state.quote.material);
  const stilesAndRailsCategory = useStore(
    (state) => state.quote.stilesAndRails,
  );
  const swingType = useStore((state) => state.quote.swingType);
  const secondaryHandle = useStore(
    (state) => state.quote.secondaryHandle,
  );
  const equalPanels = useStore((state) => state.quote.equalPanels);
  const withHandle = useStore((state) => state.quote.withHandle);
  const isPairSwing = swingType === "Pair Swing";
  const isAlum = material === "All Aluminum";
  const isPico = stilesAndRailsCategory?.includes("Pico");
  const isSingleWithSidelite =
    sType === "Single Swing" && sLite === "Add Single Sidelite";
  const isLock = exactConfigImg?.includes("LOCK");
  const hinge = exactConfig?.slice(-2);
  const selectedSide =
    hinge === "LH" && !isLock && !isSingleWithSidelite
      ? "Left Hinged"
      : hinge === "RH" && !isLock && !isSingleWithSidelite
        ? "Right Hinged"
        : hinge === "LH" && !isLock && isSingleWithSidelite
          ? "Left Hinged Handle Opposite Sidelite"
          : hinge === "RH" && !isLock && isSingleWithSidelite
            ? "Right Hinged Handle Opposite Sidelite"
            : hinge === "LH" && isLock && isSingleWithSidelite
              ? "Left Hinged Handle Facing Sidelite"
              : hinge === "RH" && isLock && isSingleWithSidelite
                ? "Right Hinged Handle Facing Sidelite"
                : "";

  const showPairWithOneHandle =
    ((withHandle === "Add Handle Set" && handleStyle === "Smart Lock *") ||
      isPico) &&
    sType === "Pair Swing" &&
    (!secondaryHandle || secondaryHandle === "No Handle");

  const showPivotLever = isPivot && handleStyle === "Dallas Lever";

  const configImgs =
    isPivot && equalPanels
      ? equalPanelsPivot
      : isPivot
        ? taggedPivotConfigs
        : isAlum && !equalPanels
          ? taggedSwingConfigsAlum
          : isAlum && equalPanels
            ? equalPanelsSwingAlum
            : showPairWithOneHandle && equalPanels
              ? taggedPicoSwingsConfigImgsEven
              : showPairWithOneHandle
                ? taggedPicoSwingsConfigImgs
                : equalPanels
                  ? equalPanelsSwing
                  : taggedSwingConfigs;

  const swingConfigImgs: SwingImgConfigObj | undefined = configImgs.find(
    (item) =>
      item.swingDirection === swingDir &&
      item.SwingType === sType &&
      item.Sidelites === sLite,
  );

  const handleClick = (side: string) => {
    if (!swingConfigImgs) return;

    if (side === "left") {
      const configIMG = showPivotLever
        ? swingConfigImgs?.img1Lever
        : swingConfigImgs?.img1;

      addToQuote({
        exactConfig: `${swingConfigImgs.label} LH`,
        exactConfigImg: configIMG,
      });

      setWidthMeasureImg(swingConfigImgs.img1M);
    }
    if (side === "right") {
      const configIMG = showPivotLever
        ? swingConfigImgs?.img2Lever
        : swingConfigImgs?.img2;

      addToQuote({
        exactConfig: `${swingConfigImgs.label} RH`,
        exactConfigImg: configIMG,
      });

      setWidthMeasureImg(swingConfigImgs.img2M);
    }
    handleError("configErr", "");
  };

  const handleClickSingleSidelite = (side: string) => {
    if (!swingConfigImgs) return;

    if (side === "left") {
      const configIMG = showPivotLever
        ? swingConfigImgs?.img3Lever
        : swingConfigImgs?.img3;

      addToQuote({
        exactConfig: `${swingConfigImgs.label} LH`,
        exactConfigImg: configIMG,
      });

      setWidthMeasureImg(swingConfigImgs.img3M);
    }
    if (side === "right") {
      const configIMG = showPivotLever
        ? swingConfigImgs?.img4Lever
        : swingConfigImgs?.img4;

      addToQuote({
        exactConfig: `${swingConfigImgs.label} RH`,
        exactConfigImg: configIMG,
      });

      setWidthMeasureImg(swingConfigImgs.img4M);
    }
    handleError("configErr", "");
  };

  useEffect(() => {
    //if stile category gets updated, then the width measure elevation image needs to be updated
    //if equal panels gets updated, then the configuration image needs to be updated
    const side = hinge === "LH" ? "left" : hinge === "RH" ? "right" : "";
    if (isPairSwing && side && swingConfigImgs) {
      if (!isLock) {
        handleClick(side);
      } else {
        handleClickSingleSidelite(side);
      }
    }
  }, [stilesAndRailsCategory, equalPanels, secondaryHandle]);

  return {
    handleClickSingleSidelite,
    handleClick,
    selectedSide,
    swingConfigImgs,
    showPivotLever,
  };
}

export default useConfigSelector;
